/**
 * ScheduleRequirementsTab — the study's "what must be true before we schedule a
 * lead" configurator (Study Hub → Scheduling). An admin picks which prescreening
 * milestones are required; every selected milestone becomes a gate on the
 * per-lead readiness checklist and warns a recruiter who schedules too early.
 *
 * Standard milestones come from the server catalog: express_interest,
 * passed_prescreen, records_received are AUTO-detected; crc_review /
 * investigator_review are manual sign-offs. A study can also add custom
 * (manual) milestones. Self-fetches GET/PUT /studies/:id/schedule-requirements;
 * recruiters see a read-only summary.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, Plus, Save, ShieldCheck, Sparkles, Trash2, X } from 'lucide-react';
import api from '../../../services/api';
import { StudyScheduleRequirements } from '../../types';

interface Props {
  studyId: string;
  isAdmin: boolean;
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';

/** A short, format-valid custom gate key: custom:<8 hex>. */
function newCustomKey(): string {
  const bytes = new Uint8Array(4);
  (window.crypto || (window as any).msCrypto).getRandomValues(bytes);
  return 'custom:' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export default function ScheduleRequirementsTab({ studyId, isAdmin }: Props) {
  const [data, setData] = useState<StudyScheduleRequirements | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  // Editable model (admin): the set of selected standard keys + custom rows.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customs, setCustoms] = useState<{ key: string; label: string }[]>([]);

  const initFrom = useCallback((d: StudyScheduleRequirements) => {
    const std = new Set(d.raw.gates.filter((k) => !k.startsWith('custom:')));
    setSelected(std);
    setCustoms(d.raw.custom.map((c) => ({ ...c })));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/schedule-requirements`);
      setData(res.data);
      initFrom(res.data);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [studyId, initFrom]);

  useEffect(() => { load(); }, [load]);

  const toggleStandard = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const addCustom = () => setCustoms((prev) => [...prev, { key: newCustomKey(), label: '' }]);
  const editCustom = (key: string, label: string) =>
    setCustoms((prev) => prev.map((c) => (c.key === key ? { ...c, label } : c)));
  const removeCustom = (key: string) => setCustoms((prev) => prev.filter((c) => c.key !== key));

  // Ordered gate list to persist: standard keys in catalog order, then customs
  // that carry a non-blank label (blank customs are dropped, same as the server).
  const buildPayload = () => {
    const orderedStandard = (data?.catalog || []).map((c) => c.key).filter((k) => selected.has(k));
    const validCustoms = customs
      .map((c) => ({ key: c.key, label: c.label.trim() }))
      .filter((c) => c.label.length > 0);
    return {
      gates: [...orderedStandard, ...validCustoms.map((c) => c.key)],
      custom: validCustoms,
    };
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    const payload = buildPayload();
    const cur = { gates: data.raw.gates, custom: data.raw.custom };
    return JSON.stringify(payload) !== JSON.stringify(cur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selected, customs]);

  const save = async () => {
    if (!isAdmin || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/api/crm/studies/${studyId}/schedule-requirements`, buildPayload());
      setData(res.data);
      initFrom(res.data);
      setSavedTick(true);
      window.setTimeout(() => setSavedTick(false), 2000);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
        {error || 'Failed to load scheduling requirements'}
      </div>
    );
  }

  const selectedCount = selected.size + customs.filter((c) => c.label.trim()).length;

  // ---- Recruiter (read-only) view ----------------------------------------
  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Scheduling requirements
        </h3>
        {data.gates.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No prescreening milestones are required before scheduling on this study.
          </p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              A lead should clear these {data.gates.length} milestone{data.gates.length === 1 ? '' : 's'} before scheduling:
            </p>
            <ul className="mt-3 space-y-1.5">
              {data.gates.map((g) => (
                <li key={g.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                  {g.label}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${g.auto ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'}`}>
                    {g.auto ? 'Auto' : 'Manual'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  // ---- Admin editor -------------------------------------------------------
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Scheduling requirements
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              Pick the prescreening milestones a lead must clear before a recruiter is justified in scheduling them.
              Each selected milestone shows up as a readiness checklist on every lead, and a recruiter who schedules
              early is warned (but can still proceed with a reason).
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
            {selectedCount} required
          </span>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Standard catalog */}
        <ul className="mt-4 space-y-2">
          {data.catalog.map((item) => {
            const on = selected.has(item.key);
            return (
              <li key={item.key}>
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors duration-150 ${
                    on
                      ? 'border-primary-300 bg-primary-50/60 dark:border-primary-800 dark:bg-primary-900/20'
                      : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleStandard(item.key)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                      <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${item.auto ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'}`}>
                        {item.auto ? <><Sparkles className="h-2.5 w-2.5" /> Auto-detected</> : <><ShieldCheck className="h-2.5 w-2.5" /> Manual sign-off</>}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{item.description}</p>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>

        {/* Custom milestones */}
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Custom milestones</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Add your own required steps (always a manual sign-off), e.g. “Cardiology clearance on file”.
          </p>
          <ul className="mt-2 space-y-2">
            {customs.map((c) => (
              <li key={c.key} className="flex items-center gap-2">
                <input
                  type="text"
                  value={c.label}
                  onChange={(e) => editCustom(c.key, e.target.value.slice(0, 80))}
                  placeholder="Describe the requirement…"
                  className="min-h-[40px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={() => removeCustom(c.key)}
                  aria-label="Remove custom requirement"
                  className="rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={addCustom}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:border-primary-400 hover:text-primary-700 dark:border-gray-600 dark:text-gray-300 dark:hover:border-primary-600 dark:hover:text-primary-400"
          >
            <Plus className="h-4 w-4" /> Add custom requirement
          </button>
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 sm:min-h-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save requirements'}
          </button>
          {dirty && !saving && (
            <button
              type="button"
              onClick={() => initFrom(data)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <X className="h-4 w-4" /> Discard changes
            </button>
          )}
          {savedTick && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-400">
              <ShieldCheck className="h-4 w-4" /> Saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
